import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import SfhSecondaryButton from './SfhSecondaryButton';
import SectionLoader from '../../Loader/SectionLoader';
import ProductImage from '../../LegacyOrder/ProductImage';
import { ReactComponent as ClockBlue } from '../../../assets/images/svg/clock-blue.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/images/svg/download.svg';
import { getCurrentAccountUid } from '../../../store/selectors/accountSelectors';
import {
  selectPortfolioSingleTransaction,
  selectPortfolioSingleTransactionError,
} from '../../../store/selectors/portfolioTransactionsSelector';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { portfolioTransactionsSingleFetch } from '../../../store/actions/portfolio/portfolioActions';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import { getSfhPurchaseLabel } from '../../../store/actions/orders/orderActions';
import { fetchDocumentByType } from '../../../store/actions/documents/documentActions';
import { accountDocumentTypes } from '../../../util/enum/api/accountDocumentTypes';
import { useBrokerCode } from '../../../util/hooks/useBrokerCode';
import { formatSfhDateTime } from '../../../util/helpers/dateHelpers';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import {
  NOT_AVAILABLE,
  FIELD_CAPTION_TYPE,
  getTransactionField,
} from './sellFromHomeTransactionHelpers';
import { mediaBelow } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import useSfhGtmHook from '../../../util/hooks/useSfhGtmHook';
import {
  SFH_VIEW_HISTORY_CLICKED,
  SFH_PACKING_SLIP_DOWNLOADED,
  SFH_SHIPPING_LABEL_DOWNLOADED,
  SFH_TX_DETAILS_CLOSE_CLICKED,
  SFH_TX_DETAILS_OPENED,
} from '../../../constants/gtmEvents';

const SUBTITLE_COLOR = themeColors.colorTextPrimary;

// Only the detail rows scroll — between the SKU divider (#2) and the bottom
// links/CTA divider (#3). The SKU section and the footer stay fixed.
const ScrollRegion = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin: 0 -10px;
`;

const HeroSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  padding: 8px 16px 16px;
  margin: 0 -16px;
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
`;

const HeroImage = styled.div`
  > img {
    width: 90px;
    height: 90px;
    object-fit: contain;
    margin: 0;
  }
`;

const HeroCaption = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${SUBTITLE_COLOR};
  text-align: center;
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
`;

const DetailLabel = styled.span`
  color: ${themeColors.colorTextPrimary};
  font-weight: 500;
`;

const DetailValue = styled.span`
  color: ${themeColors.colorTextPrimary};
  text-align: right;
`;

// Fixed bottom area below the scroll region; its top border is divider #3.
const BottomArea = styled.div`
  margin-top: auto;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  margin-left: -16px;
  margin-right: -16px;
  padding-left: 16px;
  padding-right: 16px;
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 0;
`;

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorPrimary};
  text-decoration: underline;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  > svg {
    width: 16px;
    height: 16px;
  }
`;

const FooterRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;

  > button {
    flex: 1;
    font-family: ${variables.family.goldSilver}, sans-serif;
    font-size: 16px;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: column-reverse;

    > button {
      flex: none;
    }
  }
`;

const DownloadButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${themeColors.colorPrimary};

  > svg {
    width: 16px;
    height: 16px;
  }
`;

const FetchErrorText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorError};
  text-align: center;
  margin: 8px 0 0;
`;

const SellFromHomeTransactionDetails = ({
  selectedHit,
  quantity,
  lockedSnapshot,
  handleClose,
  handleViewHistory,
  source,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sfhGtmEvent } = useSfhGtmHook();
  const brokerCode = useBrokerCode();
  const accountUid = useSelector(getCurrentAccountUid);
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const singleTransactionError = useSelector(
    selectPortfolioSingleTransactionError,
  );
  const isFetchingTransaction = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );
  const transactionUid = lockedSnapshot?.transactionUid;

  useEffect(() => {
    if (!accountUid || !transactionUid) return;
    if (singleTransaction?.Uid === transactionUid) return;
    dispatch(portfolioTransactionsSingleFetch({ accountUid, transactionUid }));
  }, [accountUid, transactionUid, dispatch, singleTransaction?.Uid]);

  const decodeHtml = (str) => {
    if (!str) return str;
    const el = document.createElement('div');
    el.innerHTML = str;
    return el.textContent;
  };

  const fields = singleTransaction?.Fields ?? [];

  // When launched from the wizard we have the selected product (selectedHit);
  // when launched from Recent Transactions we only have a Uid, so fall back to
  // the product details carried in the fetched transaction fields.
  const productImageField = fields.find((f) => f.FormatType === 'ProductImage');
  const productField = fields.find((f) => f.FormatType === 'Product');

  const productCode = selectedHit?.Code ?? productImageField?.ProductCode;
  const productCaption = selectedHit
    ? `${decodeHtml(selectedHit?.WeightCaption) ?? ''} ${
        decodeHtml(selectedHit?.Caption) ?? ''
      }`.trim()
    : decodeHtml(productField?.ProductCaption) ?? '';

  // Show the backend timestamp exactly as received (no timezone conversion);
  // only fall back to the client submission time if the BE value is missing.
  const rawDate = singleTransaction?.DateTime || lockedSnapshot?.submittedAt;
  const transactedOn = rawDate ? formatSfhDateTime(rawDate) : NOT_AVAILABLE;

  const referenceField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.REFERENCE_NUMBER,
  );
  const referenceNumber = referenceField?.Caption ?? NOT_AVAILABLE;
  const orderCode = referenceField?.Caption?.trim() || null;

  const submitterField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.SUBMITTER,
  );
  const submitter = submitterField?.Caption?.trim() || NOT_AVAILABLE;

  const summaryField = getTransactionField(fields, FIELD_CAPTION_TYPE.SUMMARY);
  const displayQuantity = summaryField?.AmountOrQuantity ?? quantity;

  const pricePerUnitField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.PRICE_PER_UNIT,
  );
  const pricePerUnit =
    pricePerUnitField != null
      ? formatMoneyNumeral(pricePerUnitField.Amount)
      : NOT_AVAILABLE;

  const orderTotalField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.ORDER_TOTAL,
  );
  const orderTotal =
    orderTotalField != null
      ? formatMoneyNumeral(orderTotalField.Amount)
      : NOT_AVAILABLE;

  const shippingFeeField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.SHIPPING_FEE,
  );
  const shippingFee =
    shippingFeeField != null
      ? formatMoneyNumeral(shippingFeeField.Amount)
      : NOT_AVAILABLE;

  const insuranceFeeField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.INSURANCE_FEE,
  );
  const insuranceFee =
    insuranceFeeField != null
      ? formatMoneyNumeral(insuranceFeeField.Amount)
      : NOT_AVAILABLE;

  const creditCardField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.CREDIT_CARD,
  );
  const creditCard = creditCardField?.Caption
    ? `****${creditCardField.Caption}`
    : NOT_AVAILABLE;

  const preAuthField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.PRE_AUTH_AMOUNT,
  );
  const preAuthAmount =
    preAuthField != null
      ? formatMoneyNumeral(preAuthField.Amount)
      : NOT_AVAILABLE;

  // StatusType is the TransactionStatusType enum key resolved in the portfolio
  // saga (e.g. 'Canceled', 'RequestSubmitted'); map it to a display label and
  // fall back to Request Submitted when it's missing/unknown.
  const statusLabel = singleTransaction?.StatusType
    ? t(
        `sellWizard.sfhWizard.transactionDetails.status_${singleTransaction.StatusType}`,
        {
          defaultValue: t(
            'sellWizard.sfhWizard.transactionDetails.statusRequestSubmitted',
          ),
        },
      )
    : t('sellWizard.sfhWizard.transactionDetails.statusRequestSubmitted');

  // Show skeletons whenever the stored transaction doesn't match the one we
  // need — covers the gap between History clearing Redux and the fetch
  // completing (isFetchingTransaction can be false for one render cycle).
  const showLoader =
    (isFetchingTransaction || singleTransaction?.Uid !== transactionUid) &&
    !singleTransactionError;

  // Returns `skeleton` while loading, otherwise the real `value`.
  const ph = (value, skeleton) => (showLoader ? skeleton : value);

  const totalChargedToday =
    shippingFeeField?.Amount != null && insuranceFeeField?.Amount != null
      ? shippingFeeField.Amount + insuranceFeeField.Amount
      : undefined;

  const gtmEventProps = {
    sku: productCode,
    quantity: displayQuantity,
    unit_price: pricePerUnitField?.Amount ?? lockedSnapshot?.unitPrice,
    total_payout: orderTotalField?.Amount ?? lockedSnapshot?.payout,
    pre_auth_amount: preAuthField?.Amount,
    shipping_fee: shippingFeeField?.Amount,
    insurance_fee: insuranceFeeField?.Amount,
    total_charged_today: totalChargedToday,
    address_selected: lockedSnapshot?.addressUid,
  };

  // "opened" only makes sense once the fetched detail data has actually
  // loaded — fire once per transaction, and only when a source is given
  // (i.e. opened from Recent Transactions/Financial Balances, not from
  // within the in-progress sell wizard).
  const openedEventFiredForRef = useRef(null);
  useEffect(() => {
    if (!source || showLoader || !transactionUid) return;
    if (openedEventFiredForRef.current === transactionUid) return;
    openedEventFiredForRef.current = transactionUid;
    sfhGtmEvent(SFH_TX_DETAILS_OPENED, { ...gtmEventProps, source });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, showLoader, transactionUid]);

  const onViewHistoryClick = () => {
    sfhGtmEvent(SFH_VIEW_HISTORY_CLICKED, gtmEventProps);
    if (typeof handleViewHistory === 'function') handleViewHistory();
  };

  const [isDownloadingPackingSlip, setIsDownloadingPackingSlip] = useState(
    false,
  );
  const [packingSlipError, setPackingSlipError] = useState('');

  const handleDownloadPackingSlip = () => {
    if (!accountUid || !orderCode || isDownloadingPackingSlip) return;
    sfhGtmEvent(SFH_PACKING_SLIP_DOWNLOADED, gtmEventProps);
    setPackingSlipError('');
    dispatch(
      fetchDocumentByType({
        accountUid,
        documentType: accountDocumentTypes.SellFromHomePackingSlip,
        documentId: orderCode,
        setIsDownloadLoading: setIsDownloadingPackingSlip,
        onError: () =>
          setPackingSlipError(
            t('sellWizard.sfhWizard.transactionDetails.packingSlipFailed'),
          ),
      }),
    );
  };

  const [isDownloadingLabel, setIsDownloadingLabel] = useState(false);
  const [shippingLabelError, setShippingLabelError] = useState('');

  const handleDownloadShippingLabel = () => {
    if (!brokerCode || !orderCode || isDownloadingLabel) return;
    sfhGtmEvent(SFH_SHIPPING_LABEL_DOWNLOADED, gtmEventProps);
    setShippingLabelError('');
    dispatch(
      getSfhPurchaseLabel({
        brokerCode,
        orderCode,
        isLoading: setIsDownloadingLabel,
        onError: () =>
          setShippingLabelError(
            t('sellWizard.sfhWizard.transactionDetails.shippingLabelFailed'),
          ),
      }),
    );
  };

  const detailRows = [
    {
      key: 'summary',
      label: t('sellWizard.sfhWizard.transactionDetails.summaryLabel'),
      value: t('sellWizard.sfhWizard.transactionDetails.summaryValue', {
        count: displayQuantity,
      }),
    },
    {
      key: 'transactedOn',
      label: t('sellWizard.sfhWizard.transactionDetails.transactedOn'),
      value: ph(transactedOn, formatSfhDateTime(new Date())),
    },
    {
      key: 'referenceNumber',
      label: t('sellWizard.sfhWizard.transactionDetails.referenceNumber'),
      value: ph(referenceNumber, 'SFH-——————'),
    },
    {
      key: 'status',
      label: t('sellWizard.sfhWizard.transactionDetails.status'),
      value: ph(
        statusLabel,
        t('sellWizard.sfhWizard.transactionDetails.statusRequestSubmitted'),
      ),
    },
    {
      key: 'submitter',
      label: t('sellWizard.sfhWizard.transactionDetails.submitter'),
      value: ph(submitter, '——'),
    },
    {
      key: 'pricePerUnit',
      label: t('sellWizard.sfhWizard.transactionDetails.pricePerUnit'),
      value: ph(
        pricePerUnit,
        lockedSnapshot?.unitPrice != null
          ? formatMoneyNumeral(lockedSnapshot.unitPrice)
          : '$—.——',
      ),
    },
    {
      key: 'orderTotal',
      label: t('sellWizard.sfhWizard.transactionDetails.orderTotal'),
      value: ph(
        orderTotal,
        lockedSnapshot?.payout != null
          ? formatMoneyNumeral(lockedSnapshot.payout)
          : '$—.——',
      ),
    },
    {
      key: 'preAuthAmount',
      label: t('sellWizard.sfhWizard.transactionDetails.preAuthAmount'),
      value: ph(preAuthAmount, '$—.——'),
    },
    {
      key: 'shippingFee',
      label: t('sellWizard.sfhWizard.transactionDetails.shippingFee'),
      value: ph(shippingFee, '$—.——'),
    },
    {
      key: 'insuranceFee',
      label: t('sellWizard.sfhWizard.transactionDetails.insuranceFee'),
      value: ph(insuranceFee, '$—.——'),
    },
    {
      key: 'creditCard',
      label: t('sellWizard.sfhWizard.transactionDetails.creditCard'),
      value: ph(creditCard, '****——'),
    },
  ];

  return (
    <SectionLoader isLoading={showLoader}>
      <ModalBody>
        <HeroSection>
          <HeroImage>
            <ProductImage productCode={productCode} width={90} height={90} />
          </HeroImage>
          <HeroCaption>{productCaption}</HeroCaption>
        </HeroSection>

        <ScrollRegion>
          <DetailsSection>
            {detailRows.map(({ key, label, value }) => (
              <DetailRow key={key}>
                <DetailLabel>{label}</DetailLabel>
                <DetailValue>{value}</DetailValue>
              </DetailRow>
            ))}
          </DetailsSection>
        </ScrollRegion>

        <BottomArea>
          <LinkRow>
            <LinkButton
              type="button"
              onClick={onViewHistoryClick}
              data-cy="link-sfh-view-history"
            >
              <ClockBlue />
              {t('sellWizard.sfhWizard.transactionDetails.viewHistory')}
            </LinkButton>
            <LinkButton
              type="button"
              onClick={handleDownloadPackingSlip}
              disabled={!orderCode || isDownloadingPackingSlip}
              data-cy="link-sfh-download-packing-slip"
            >
              <DownloadIcon />
              {t('sellWizard.sfhWizard.transactionDetails.downloadPackingSlip')}
            </LinkButton>
          </LinkRow>

          {singleTransactionError && (
            <FetchErrorText data-cy="text-sfh-transaction-fetch-error">
              {t('sellWizard.sfhWizard.transactionDetails.fetchFailed')}
            </FetchErrorText>
          )}

          {packingSlipError && (
            <FetchErrorText data-cy="text-sfh-packing-slip-error">
              {packingSlipError}
            </FetchErrorText>
          )}

          {shippingLabelError && (
            <FetchErrorText data-cy="text-sfh-shipping-label-error">
              {shippingLabelError}
            </FetchErrorText>
          )}

          <FooterRow>
            <SfhSecondaryButton
              onClick={() => {
                sfhGtmEvent(SFH_TX_DETAILS_CLOSE_CLICKED, gtmEventProps);
                handleClose();
              }}
              data-cy="button-sfh-transaction-close"
            >
              {t('sellWizard.sfhWizard.transactionDetails.close')}
            </SfhSecondaryButton>
            <SfhSecondaryButton
              onClick={handleDownloadShippingLabel}
              disabled={!orderCode || isDownloadingLabel}
              data-cy="button-sfh-download-shipping-label"
            >
              <DownloadButtonContent>
                <DownloadIcon />
                {t(
                  'sellWizard.sfhWizard.transactionDetails.downloadShippingLabel',
                )}
              </DownloadButtonContent>
            </SfhSecondaryButton>
          </FooterRow>
        </BottomArea>
      </ModalBody>
    </SectionLoader>
  );
};

SellFromHomeTransactionDetails.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
  }),
  quantity: PropTypes.number,
  lockedSnapshot: PropTypes.shape({
    transactionUid: PropTypes.string,
    submittedAt: PropTypes.string,
    unitPrice: PropTypes.number,
    payout: PropTypes.number,
    addressUid: PropTypes.string,
  }),
  handleClose: PropTypes.func,
  handleViewHistory: PropTypes.func,
  source: PropTypes.string,
};

export default SellFromHomeTransactionDetails;
